import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import useOnClickOutside from 'use-onclickoutside';

import {
  useCalculateBottomFooter,
  useChangeDocumentTitleSeenMessage,
  useChatChannelData,
  useReceiveNewMessage,
  useSplitChatChannelListOrder,
} from './hooks';
import { requestNotificationPermission } from '../../utils/utils';
import {
  closeChatChannel,
  openChatChannel,
  receiveNewMessage,
} from './actions';
import {
  checkChannelHasUnreadMessage,
  checkListChannelHasUnreadMessage,
} from './routines';
import ChatChannel from './ChatChannel/ChatChannel';

import './ChatBox.scss';
import { EnumChatAppType, findEnumById } from '../../constants/enum';

const HiddenChatChannelList = ({
  isOpen,
  handleClose,
  chatChannelIds,
  chatChannelData,
}) => {
  const dispatch = useDispatch();

  const ref = useRef();
  useOnClickOutside(ref, handleClose);
  return (
    isOpen && (
      <div className="hidden-chat-channel-list" ref={ref}>
        {chatChannelIds.map((channelId) => {
          const channelData = chatChannelData[channelId];
          const { appType } = channelData;
          const enumAppType = findEnumById(EnumChatAppType, appType);
          const isHasUnreadMessage = checkChannelHasUnreadMessage(channelData);
          return (
            <div
              key={channelId}
              className={`hidden-chat-channel-item ${
                isHasUnreadMessage ? 'has-new-message blink' : ''
              }`}
              onClick={() => dispatch(openChatChannel(channelId, true))}
            >
              <span className="title">
                <span className="font-weight-bold">
                  {`#${channelId} (${enumAppType.label})`}
                </span>
              </span>
              <i
                className="fa fa-times-circle"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(closeChatChannel(channelId));
                }}
              />
            </div>
          );
        })}
      </div>
    )
  );
};

const HiddenChatChannelButton = ({ hiddenChatChannel }) => {
  const [isListChannelPopupOpen, setIsListChannelPopupOpen] = useState(false);
  const toggleListChannelPopup = () => {
    setIsListChannelPopupOpen(isOpen => !isOpen);
  };
  const chatChannelData = useChatChannelData(hiddenChatChannel);
  const isHasUnreadMessage = checkListChannelHasUnreadMessage(chatChannelData);
  return (
    hiddenChatChannel.length > 0 && (
      <div
        className="hidden-chat-channel-button"
        onClick={toggleListChannelPopup}
      >
        <i
          className={`fa fa-comments ${
            isHasUnreadMessage ? 'has-new-message' : ''
          }`}
        />
        <HiddenChatChannelList
          isOpen={isListChannelPopupOpen}
          handleClose={() => setIsListChannelPopupOpen(false)}
          chatChannelIds={hiddenChatChannel}
          chatChannelData={chatChannelData}
        />
      </div>
    )
  );
};

const ChatBox = memo(() => {
  const dispatch = useDispatch();
  const mapState = useCallback(state => ({
    activeChannel: state.chat.activeChannel,
    chatChannelListOrder: state.chat.chatChannelListOrder,
  }), []);
  const { activeChannel, chatChannelListOrder } = useMappedState(mapState);

  const wrapperRef = useCalculateBottomFooter();

  useReceiveNewMessage((appType, message) =>
    dispatch(receiveNewMessage(appType, message)),
  );

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const [hiddenChatChannel, visibleChatChannel] = useSplitChatChannelListOrder(
    chatChannelListOrder,
  );

  // change document title base on chat message seen status
  useChangeDocumentTitleSeenMessage();

  return (
    <div className="chat-box-wrapper" ref={wrapperRef}>
      <HiddenChatChannelButton hiddenChatChannel={hiddenChatChannel} />
      {[...visibleChatChannel].reverse().map(channelId => (
        <ChatChannel
          key={channelId}
          id={channelId}
          isActive={activeChannel === channelId}
        />
      ))}
    </div>
  );
});

export default ChatBox;



// WEBPACK FOOTER //
// ./foody/src/pages/ChatBox/ChatBox.jsx